import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { selectLoginData, selectLoginStatus, selectLoginError, initializeAsync } from "./loginSlice";

import {
    Button,
    CssBaseline,
    TextField,
    Box,
    Typography,
    Container,
    Paper,
    Divider,
    Link,
    Snackbar,
    Alert,
} from "@mui/material";

import LoadingButton from "@mui/lab/LoadingButton";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import "./style.css";
import { SimpleHeader } from "../../components/SimpleHeader";
import { getAllUrlParams } from "../../utils";
import { get, post } from "../../api";

const Login = (props) => {
    const data = useSelector(selectLoginData);
    const status = useSelector(selectLoginStatus);
    const error = useSelector(selectLoginError);
    const dispatch = useDispatch();
    const [openToster, setOpenToster] = useState(false);
    const [tosterVariant, setTosterVariant] = useState("error");
    const [tosterMessage, setTosterMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [displayBox, setDisplayBox] = useState(false);
    const { application } = props;

    const query = getAllUrlParams(window.location.search);
    const defaultEmail = query?.email;

    const [authenticated, setAuthenticated] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const redirectLocation = location.state?.path || "/";

    let paramsData = redirectLocation ? getAllUrlParams(redirectLocation) : {};
    let type = paramsData.type ? paramsData.type : "";
    let recordId = paramsData.recordId ? paramsData.recordId : "";

    const { user, passwordLessLogin, getUserDetailsNotAsync, login, isAuthenticated } = useAuth();

    useEffect(() => {
        if (status === "idle") {
            dispatch(initializeAsync());
        }
    }, [status, dispatch]);

    React.useEffect(() => {
        setTimeout(() => {
            setDisplayBox(true);
        }, 1500);
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            setLoaded(false);
            try {
                let value = await isAuthenticated();
                setAuthenticated(value);
            } catch {
                setAuthenticated(false);
            } finally {
                setLoaded(true);
            }
        };
        checkAuth();
    }, [location.pathname]);

    if (!loaded) {
        return null; // You can render a loading spinner or message here
    }

    if (authenticated) {
        let userData = getUserDetailsNotAsync();
        if (user && userData?.contact) {
            navigate("/", {
                replace: true,
            });
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        const formData = new FormData(event.currentTarget);
        let record = {};
        record.email = formData.get("email");
        let authenticated = await isAuthenticated();
        if (authenticated) {
            window.location.href = "/";
            return;
        }
        post(`contact/checkExistingUser`, record).then(async (result) => {
            console.log("Check Existing user :: ", result.isSuccess + "  " + result.data);
            if (result.isSuccess && result.data === true) {
                if (data.authenticationMode === "OTP") {
                    passwordLessLogin({
                        email: formData.get("email"),
                        callback: (failed) => {
                            if (failed) {
                                setTosterVariant("error");
                                setTosterMessage(`Sorry, we don't recognize this email address`);
                                setOpenToster(true);
                            }
                            setLoading(false);
                        },
                    });
                } else {
                    login({ email: formData.get("email"), password: formData.get("password") });
                }
            } else {
                navigate("/register", {
                    replace: true,
                    state: {
                        path: redirectLocation,
                        searchParams: { email: formData.get("email"), type: type, recordId: recordId },
                    },
                });
            }
        });
    };

    const handleTosterClose = () => {
        setOpenToster(false);
    };

    let body = (
        <Box
            component="main"
            sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                backgroundColor: "black",
            }}>
            <SimpleHeader></SimpleHeader>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexGrow: 1,
                    minHeight: "100%",
                    backgroundColor: "black",
                }}>
                <Container
                    className={displayBox ? "fade-in show" : "fade-in"}
                    maxWidth="sm"
                    sx={{ maxWidth: "450px !important", justifyContent: "center", mt: "-6rem" }}>
                    <CssBaseline />
                    <Paper
                        elevation={16}
                        sx={{
                            backgroundColor: "rgb(255, 255, 255)",
                            color: "rgb(18, 24, 40)",
                            transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                            borderRadius: "0px",
                            boxShadow: "rgb(100 116 139 / 12%) 0px 10px 15px",
                            backgroundImage: "none",
                            overflow: "hidden",
                            padding: "32px",
                        }}>
                        <Box sx={{ textAlign: "center" }}>
                            <Typography component="h1" variant="h4">
                                Sign in
                            </Typography>
                            <Typography
                                align="center"
                                color="textSecondary"
                                variant="subtitle1"
                                margin="1rem"
                                fontSize="0.875rem"
                                sx={{ fontWeight: "400", lineHeight: "20px" }}>
                                Welcome to the Fresh Artists Portal. Enter your email to log in. We'll send you a
                                one-time passcode to your email.
                            </Typography>
                        </Box>
                        <Box component="form" onSubmit={handleSubmit} validate sx={{ mt: 1 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                type="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                defaultValue={defaultEmail}
                                sx={{ borderRadius: "8px", height: "auto" }}
                                variant="outlined"
                            />

                            <LoadingButton
                                size="small"
                                type="submit"
                                color="dark"
                                fullWidth
                                loading={loading}
                                loadingPosition="end"
                                variant="contained"
                                sx={{ mt: 3, mb: 2, height: "45px" }}>
                                Sign In
                            </LoadingButton>
                            {/* <Button
                                color="primary"
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2, height: "45px" }}
                            >
                                Sign In
                            </Button> */}
                        </Box>
                    </Paper>
                    <Grid container sx={{ marginTop: "-2rem", width: "100%" }}>
                        <Grid item xs={4} sx={{ height: "10px", background: "white" }}></Grid>
                        <Grid item xs={4} sx={{ height: "30px", background: "white" }}></Grid>
                        <Grid item xs={4} sx={{ height: "50px", background: "white" }}></Grid>
                    </Grid>
                </Container>
            </Box>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                open={openToster}
                autoHideDuration={6000}
                onClose={handleTosterClose}>
                <Alert onClose={handleTosterClose} severity={tosterVariant} variant="filled">
                    {tosterMessage}
                </Alert>
            </Snackbar>
        </Box>
    );

    let content;
    if (status === "loading") {
        content = <p>Loading...</p>;
    } else if (status === "succeeded") {
        content = body;
    } else if (status === "failed") {
        content = <p>Error : {error}</p>;
    }

    return content;
};

export { Login };
